import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Search, ChevronDown, ChevronRight, ShoppingBag } from 'lucide-react';
import Logo from './Logo';
import { NAV_LINKS, COLLECTIONS_BY_BRAND } from '../mock';
import { BRANDS } from '../data/brands';
import { useCart } from '../context/CartContext';

const slugifyBrand = (b) =>
  b.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [makesOpen, setMakesOpen] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [hoveredBrand, setHoveredBrand] = useState(null);
  const [mobileMakesOpen, setMobileMakesOpen] = useState(false);
  const [mobileBrandsOpen, setMobileBrandsOpen] = useState(false);
  const [mobileBrandOpen, setMobileBrandOpen] = useState(null);
  const dropdownRef = useRef(null);
  const brandsDropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setOpen(false);
    setMakesOpen(false);
    setBrandsOpen(false);
    setHoveredBrand(null);
    setMobileMakesOpen(false);
    setMobileBrandsOpen(false);
    setMobileBrandOpen(null);
  }, [location.pathname]);

  // Close desktop dropdown on outside click
  useEffect(() => {
    if (!makesOpen) return;
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setMakesOpen(false);
        setHoveredBrand(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [makesOpen]);

  // Close brands dropdown on outside click
  useEffect(() => {
    if (!brandsOpen) return;
    const handleClickOutside = (e) => {
      if (brandsDropdownRef.current && !brandsDropdownRef.current.contains(e.target)) {
        setBrandsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [brandsOpen]);

  // Header is always dark — white logo + white text on every page.
  const headerBg = scrolled
    ? 'bg-black/85 backdrop-blur border-b border-white/10'
    : 'bg-black';

  const linkBase = 'text-white/80 hover:text-white';
  const activeColor = 'text-[#FF6B00]';

  const brands = Object.keys(COLLECTIONS_BY_BRAND).sort();
  const { itemCount, openCart } = useCart();

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${headerBg}`}>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 lg:py-6 flex items-center justify-between">
        <Link to="/" aria-label="Home">
          <Logo variant="light" />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => {
            if (l.label === 'SHOP BY VEHICLE') {
              return (
                <div key={l.path} ref={dropdownRef} className="relative">
                  <button
                    type="button"
                    data-testid="header-shop-by-vehicle-toggle"
                    onClick={() => setMakesOpen((o) => !o)}
                    className={`flex items-center gap-1 text-[15px] font-display font-semibold tracking-[0.22em] transition-colors ${
                      makesOpen || location.pathname.startsWith('/shop') ? activeColor : linkBase
                    }`}
                  >
                    {l.label}
                    <ChevronDown size={16} className={`transition-transform ${makesOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {makesOpen && (
                    <div
                      className="absolute top-full left-0 mt-2 flex bg-black border border-white/10 shadow-2xl"
                      data-testid="header-shop-dropdown"
                      onMouseLeave={() => setHoveredBrand(null)}
                    >
                      {/* Makes column */}
                      <ul className="py-2 min-w-[240px] max-h-[70vh] overflow-y-auto border-r border-white/10">
                        <li className="px-5 pt-3 pb-2 border-b border-white/10 mb-2">
                          <div className="font-display tracking-[0.3em] text-[10px] text-[#FF6B00] font-semibold">
                            SELECT A MAKE
                          </div>
                        </li>
                        {brands.map((brand) => {
                          const models = COLLECTIONS_BY_BRAND[brand] || [];
                          const hasModels = models.length > 0;
                          const isActive = hoveredBrand === brand;
                          return (
                            <li
                              key={brand}
                              onMouseEnter={() => setHoveredBrand(brand)}
                            >
                              <div className="flex items-center group">
                                <Link
                                  to={`/shop/${slugifyBrand(brand)}`}
                                  onClick={() => setMakesOpen(false)}
                                  data-testid={`header-make-${slugifyBrand(brand)}`}
                                  className={`flex-1 px-5 py-2.5 font-display tracking-[0.18em] text-[13px] transition-colors ${
                                    isActive ? 'bg-white/5 text-[#FF6B00]' : 'text-white/85 hover:text-[#FF6B00] hover:bg-white/5'
                                  }`}
                                >
                                  {brand.toUpperCase()}
                                  <span className="ml-2 text-[10px] text-white/40">
                                    {models.length}
                                  </span>
                                </Link>
                                {hasModels && (
                                  <ChevronRight size={14} className={`mr-3 transition-colors ${isActive ? 'text-[#FF6B00]' : 'text-white/30'}`} />
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>

                      {/* Models column (shown when a make is hovered) */}
                      {hoveredBrand && (COLLECTIONS_BY_BRAND[hoveredBrand] || []).length > 0 && (
                        <ul className="py-2 min-w-[280px] max-h-[70vh] overflow-y-auto">
                          <li className="px-5 pt-3 pb-2 border-b border-white/10 mb-2">
                            <div className="font-display tracking-[0.3em] text-[10px] text-[#FF6B00] font-semibold">
                              {hoveredBrand.toUpperCase()} MODELS
                            </div>
                          </li>
                          {(COLLECTIONS_BY_BRAND[hoveredBrand] || []).map((m) => (
                            <li key={m.id}>
                              <Link
                                to={`/collections/${m.id}`}
                                onClick={() => setMakesOpen(false)}
                                data-testid={`header-model-${m.id}`}
                                className="block px-5 py-2 font-display tracking-[0.15em] text-[12px] text-white/85 hover:text-[#FF6B00] hover:bg-white/5 transition-colors"
                              >
                                {m.name.toUpperCase()}
                                {m.years && m.years !== '—' && (
                                  <span className="ml-1.5 text-white/40 tracking-normal">({m.years})</span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            if (l.label === 'SHOP BY BRANDS') {
              return (
                <div key={l.path} ref={brandsDropdownRef} className="relative">
                  <button
                    type="button"
                    data-testid="header-shop-by-brands-toggle"
                    onClick={() => setBrandsOpen((o) => !o)}
                    className={`flex items-center gap-1 text-[15px] font-display font-semibold tracking-[0.22em] transition-colors ${
                      brandsOpen || location.pathname.startsWith('/brands') ? activeColor : linkBase
                    }`}
                  >
                    {l.label}
                    <ChevronDown size={16} className={`transition-transform ${brandsOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {brandsOpen && (
                    <div
                      className="absolute top-full left-0 mt-2 bg-black border border-white/10 shadow-2xl"
                      data-testid="header-brands-dropdown"
                    >
                      <ul className="py-2 min-w-[320px] max-h-[70vh] overflow-y-auto">
                        <li className="px-5 pt-3 pb-2 border-b border-white/10 mb-2">
                          <div className="font-display tracking-[0.3em] text-[10px] text-[#FF6B00] font-semibold">
                            SELECT A BRAND
                          </div>
                        </li>
                        {BRANDS.map((b) => (
                          <li key={b.slug}>
                            <Link
                              to={`/brands/${b.slug}`}
                              onClick={() => setBrandsOpen(false)}
                              data-testid={`header-brand-${b.slug}`}
                              className="group flex items-center justify-between gap-6 px-5 py-2.5 hover:bg-white/5 transition-colors"
                            >
                              <span className="min-w-0">
                                <span className="block font-display tracking-[0.18em] text-[13px] text-white/85 group-hover:text-[#FF6B00] transition-colors">
                                  {b.name.toUpperCase()}
                                </span>
                                <span className="block text-[10px] tracking-[0.15em] text-white/40 mt-0.5 truncate">
                                  {b.kicker.toUpperCase()}
                                </span>
                              </span>
                              <ChevronRight size={14} className="text-white/30 group-hover:text-[#FF6B00] transition-colors flex-shrink-0" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={l.path}
                to={l.path}
                end={l.path === '/'}
                className={({ isActive }) =>
                  `text-[15px] font-display font-semibold tracking-[0.22em] transition-colors ${isActive ? activeColor : linkBase}`
                }
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/search"
            aria-label="Search products"
            className="h-10 w-10 flex items-center justify-center border-2 border-white/30 text-white/80 hover:border-white hover:text-white transition-colors"
          >
            <Search size={18} />
          </Link>
          <button
            type="button"
            onClick={openCart}
            data-testid="header-cart-button"
            aria-label={`Open cart with ${itemCount} items`}
            className="relative h-10 w-10 flex items-center justify-center border-2 border-white/30 text-white/80 hover:border-white hover:text-white transition-colors"
          >
            <ShoppingBag size={18} />
            {itemCount > 0 && (
              <span
                data-testid="header-cart-badge"
                className="absolute -top-1.5 -right-1.5 bg-[#FF6B00] text-white text-[10px] font-display tracking-wide font-bold h-5 min-w-[20px] px-1 flex items-center justify-center rounded-full"
              >
                {itemCount}
              </span>
            )}
          </button>
          <Link
            to="/contact"
            className="font-display tracking-[0.2em] text-[14px] font-bold px-6 py-3 border-2 border-[#FF6B00] text-white hover:bg-[#FF6B00] transition-colors"
          >
            GET A QUOTE
          </Link>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <button
            type="button"
            onClick={openCart}
            aria-label={`Open cart with ${itemCount} items`}
            className="relative h-10 w-10 flex items-center justify-center text-white"
          >
            <ShoppingBag size={22} />
            {itemCount > 0 && (
              <span className="absolute top-0 right-0 bg-[#FF6B00] text-white text-[10px] font-display tracking-wide font-bold h-4 min-w-[16px] px-1 flex items-center justify-center rounded-full">
                {itemCount}
              </span>
            )}
          </button>
          <button
            className="text-white"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="lg:hidden border-t bg-black/95 border-white/10 max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => {
              if (l.label === 'SHOP BY VEHICLE') {
                return (
                  <div key={l.path}>
                    <button
                      type="button"
                      onClick={() => setMobileMakesOpen((o) => !o)}
                      className={`w-full flex items-center justify-between font-display tracking-[0.22em] text-sm font-semibold ${
                        mobileMakesOpen ? activeColor : linkBase
                      }`}
                    >
                      <span>{l.label}</span>
                      <ChevronDown size={16} className={`transition-transform ${mobileMakesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileMakesOpen && (
                      <ul className="mt-3 pl-4 border-l-2 border-white/10 space-y-1">
                        {brands.map((brand) => {
                          const models = COLLECTIONS_BY_BRAND[brand] || [];
                          const open = mobileBrandOpen === brand;
                          return (
                            <li key={brand}>
                              <div className="flex items-center justify-between">
                                <Link
                                  to={`/shop/${slugifyBrand(brand)}`}
                                  className="flex-1 py-2 font-display tracking-[0.18em] text-[12px] text-neutral-300"
                                >
                                  {brand.toUpperCase()}
                                  <span className="ml-2 text-[10px] text-neutral-500">
                                    {models.length}
                                  </span>
                                </Link>
                                {models.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => setMobileBrandOpen(open ? null : brand)}
                                    className="p-2 text-white/60"
                                    aria-label={`Toggle ${brand} models`}
                                  >
                                    <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                                  </button>
                                )}
                              </div>
                              {open && (
                                <ul className="pl-3 pb-2 border-l border-white/10 ml-2 space-y-1.5">
                                  {models.map((m) => (
                                    <li key={m.id}>
                                      <Link
                                        to={`/collections/${m.id}`}
                                        className="block font-display tracking-[0.15em] text-[11px] text-neutral-400 hover:text-[#FF6B00]"
                                      >
                                        {m.name.toUpperCase()}
                                        {m.years && m.years !== '—' && (
                                          <span className="ml-1 text-neutral-500 tracking-normal">({m.years})</span>
                                        )}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              }
              if (l.label === 'SHOP BY BRANDS') {
                return (
                  <div key={l.path}>
                    <button
                      type="button"
                      onClick={() => setMobileBrandsOpen((o) => !o)}
                      data-testid="header-mobile-brands-toggle"
                      className={`w-full flex items-center justify-between font-display tracking-[0.22em] text-sm font-semibold ${
                        mobileBrandsOpen ? activeColor : linkBase
                      }`}
                    >
                      <span>{l.label}</span>
                      <ChevronDown size={16} className={`transition-transform ${mobileBrandsOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileBrandsOpen && (
                      <ul className="mt-3 pl-4 border-l-2 border-white/10 space-y-1">
                        {BRANDS.map((b) => (
                          <li key={b.slug}>
                            <Link
                              to={`/brands/${b.slug}`}
                              data-testid={`header-mobile-brand-${b.slug}`}
                              className="block py-2 font-display tracking-[0.18em] text-[12px] text-neutral-300 hover:text-[#FF6B00]"
                            >
                              {b.name.toUpperCase()}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={l.path}
                  to={l.path}
                  end={l.path === '/'}
                  className={({ isActive }) =>
                    `font-display tracking-[0.22em] text-sm font-semibold ${isActive ? activeColor : linkBase}`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
            <Link
              to="/search"
              className={`font-display tracking-[0.22em] text-sm font-semibold ${linkBase}`}
            >
              SEARCH
            </Link>
            <Link
              to="/contact"
              className="mt-2 text-center font-display tracking-[0.2em] text-sm font-bold px-5 py-3 border-2 border-[#FF6B00] text-[#FF6B00]"
            >
              GET A QUOTE
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
